import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { loadWishlist, clearWishlistLocal } from "./wishlistSlice";
import { fetchCart, clearCartLocal } from "./cartSlice";
// ✅ Environment-based API URL
const API_URL = process.env.REACT_APP_API_URL;

// Retrieve cached user for UI state (non-sensitive)
const storedUser = JSON.parse(localStorage.getItem("user")) || null;

/* ============================
   🔹 REGISTER USER
============================ */
export const registerUser = createAsyncThunk(
  "auth/registerUser",
  async (userData, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post(`${API_URL}/auth/register`, userData, {
        withCredentials: true,
      });

      localStorage.setItem("user", JSON.stringify(response.data.user));
      localStorage.setItem("token", response.data.token);

      dispatch(loadWishlist());
      dispatch(fetchCart());

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Registration failed");
    }
  }
);

/* ============================
   🔹 LOGIN USER
============================ */
export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (userData, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, userData, {
        withCredentials: true,
      });

      localStorage.setItem("user", JSON.stringify(response.data.user));
      localStorage.setItem("token", response.data.token);

      dispatch(loadWishlist());
      dispatch(fetchCart());

      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Login failed. Please try again.";
      const action = error.response?.data?.action || null;
      return rejectWithValue({ message: errorMessage, action });
    }
  }
);

/* ============================
   🔹 CHECK AUTH STATUS
============================ */
export const checkAuthStatus = createAsyncThunk(
  "auth/checkAuthStatus",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");

      const response = await axios.get(`${API_URL}/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });

      localStorage.setItem("user", JSON.stringify(response.data.user));

      dispatch(loadWishlist());
      dispatch(fetchCart());

      return response.data.user;
    } catch (error) {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      dispatch(clearWishlistLocal());
      dispatch(clearCartLocal());
      return rejectWithValue("User not authenticated");
    }
  }
);

/* ============================
   🔹 RESEND VERIFICATION EMAIL
============================ */
export const resendVerificationEmail = createAsyncThunk(
  "auth/resendVerificationEmail",
  async (email, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${API_URL}/auth/resend-verification`,
        { email },
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to send verification email.");
    }
  }
);

/* ============================
   🔹 LOGOUT USER
============================ */
export const logoutUser = createAsyncThunk("auth/logoutUser", async (_, { dispatch }) => {
  localStorage.removeItem("user");
  localStorage.removeItem("token");
  dispatch(clearWishlistLocal());
  dispatch(clearCartLocal());

  try {
    await axios.post(`${API_URL}/auth/logout`, {}, { withCredentials: true });
  } catch (error) {
    // ignore backend logout failure
  }

  return null;
});

/* ============================
   🔹 UPDATE PROFILE
============================ */
export const updateProfile = createAsyncThunk(
  "auth/updateProfile",
  async (userData, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(`${API_URL}/users/update-profile`, userData, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });

      const storedUser = JSON.parse(localStorage.getItem("user"));
      const updatedUser = { ...storedUser, ...response.data };
      localStorage.setItem("user", JSON.stringify(updatedUser));

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Profile update failed");
    }
  }
);

/* ============================
   🔹 RESET PASSWORD
============================ */
export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async ({ token, newPassword }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_URL}/auth/reset-password`, {
        token,
        newPassword,
      });
      return response.data.message;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Password reset failed");
    }
  }
);

/* ============================
   🔹 GOOGLE LOGIN (SUPABASE + BACKEND)
============================ */
export const loginWithGoogle = createAsyncThunk(
  "auth/loginWithGoogle",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      // Start Google OAuth login via backend
      window.location.href = `${API_URL}/auth/google`;
    } catch (error) {
      return rejectWithValue("Google login failed. Try again.");
    }
  }
);


/* ============================
   🔹 SLICE
============================ */
const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: storedUser,
    loading: false,
    error: null,
    successMessage: null,
  },
  reducers: {
    clearAuthError: (state) => { state.error = null; },
    clearSuccessMessage: (state) => { state.successMessage = null; },
  },
  extraReducers: (builder) => {
    builder
      // Check Auth
      .addCase(checkAuthStatus.pending, (state) => { state.loading = true; })
      .addCase(checkAuthStatus.fulfilled, (state, action) => { state.loading = false; state.user = action.payload; })
      .addCase(checkAuthStatus.rejected, (state) => { state.loading = false; state.user = null; })

      // Login
      .addCase(loginUser.pending, (state) => { state.loading = true; })
      .addCase(loginUser.fulfilled, (state, action) => { state.loading = false; state.user = action.payload.user; })
      .addCase(loginUser.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      // Google Login
      .addCase(loginWithGoogle.pending, (state) => { state.loading = true; })
      .addCase(loginWithGoogle.fulfilled, (state, action) => { state.loading = false; state.user = action.payload.user; })
      .addCase(loginWithGoogle.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      // Register
      .addCase(registerUser.pending, (state) => { state.loading = true; })
      .addCase(registerUser.fulfilled, (state, action) => { state.loading = false; state.user = action.payload.user; })
      .addCase(registerUser.rejected, (state, action) => { state.loading = false; state.error = action.payload; })

      // Logout
      .addCase(logoutUser.fulfilled, (state) => { state.user = null; state.loading = false; })

      // Update Profile
      .addCase(updateProfile.fulfilled, (state, action) => { state.user = { ...state.user, ...action.payload }; state.loading = false; })

      // Reset Password
      .addCase(resetPassword.fulfilled, (state, action) => { state.successMessage = action.payload; state.loading = false; })

      // Resend Verification
      .addCase(resendVerificationEmail.fulfilled, (state, action) => { state.successMessage = action.payload?.message || "Verification email sent."; state.loading = false; });
  },
});

export const { clearAuthError, clearSuccessMessage } = authSlice.actions;
export default authSlice.reducer;
